// Copyright 2025, University of Maryland, All Rights Reserved
/**
 * Types for node inheritance tracking.
 *
 * When a node extends a base node, the backend tracks which fields
 * were explicitly overridden vs inherited. This enables the frontend to
 * show inheritance indicators and "reset to inherited" functionality.
 */

/**
 * Inheritance metadata from backend runtime.
 * Present when node extends a base node via the extends pattern.
 */
export interface InheritanceMetadata {
  /** Workspace ID of the base node (e.g., 'contract-review') */
  sourceWs: string
  /** Instance ID of the base node being extended */
  sourceInstanceId: string
  /** Field paths explicitly set in child (e.g., ['settings.temperature']) */
  overriddenFields: string[]
}

/**
 * Extract inheritance metadata from runtime object.
 * Backend sends camelCase, so we look for 'inheritance' key.
 */
export function extractInheritance(
  runtime: Record<string, unknown> | undefined
): InheritanceMetadata | undefined {
  if (!runtime) return undefined
  const inheritance = runtime.inheritance as Record<string, unknown> | undefined
  if (!inheritance) return undefined

  return {
    sourceWs: inheritance.sourceWs as string,
    sourceInstanceId: inheritance.sourceInstanceId as string,
    overriddenFields: (inheritance.overriddenFields as string[]) ?? [],
  }
}
